import { inject } from '@angular/core';
import { NavigationError } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { _t } from '../services/translation/helpers/preview-text.function';

/** When this tab last reloaded because a navigation failed. */
const RELOAD_MARKER = 'ic-navigation-reload';

/**
 * How recent a previous reload has to be for the next failure to count as the
 * same problem. Short enough that an unrelated failure later still gets its own
 * reload, long enough that a page failing on every attempt stops looping.
 */
const RELOAD_COOLDOWN_MS = 30_000;

/**
 * A page can fail to load for reasons that have nothing to do with its code:
 * a deploy replaced the files while the tab was open, the connection dropped
 * mid-request, a cached file is corrupt. Angular leaves the navigation
 * unfinished, which on screen is a spinner that never stops and says nothing.
 *
 * Most of those cases are fixed by loading the application again, so the first
 * failure reloads once. If it fails again straight away the reload is not the
 * answer, and the user gets a message they can act on rather than a spinner.
 */
export function handleNavigationError(error: NavigationError): void {
  const snackBar = inject(MatSnackBar);

  if (typeof window === 'undefined') {
    return;
  }

  let lastReload: number;
  try {
    lastReload = Number(window.sessionStorage?.getItem(RELOAD_MARKER)) || 0;
  } catch {
    // Private windows and blocked storage throw on access. Without somewhere to
    // record the attempt a reload could loop, so treat one as just done.
    lastReload = Date.now();
  }

  if (Date.now() - lastReload > RELOAD_COOLDOWN_MS) {
    try {
      window.sessionStorage?.setItem(RELOAD_MARKER, String(Date.now()));
      window.location.reload();
      return;
    } catch {
      // Fall through to the message below.
    }
  }

  console.error('Navigation failed:', error?.error ?? error);

  snackBar
    .open(_t('navigation_error_message'), _t('action_refresh'))
    .onAction()
    .subscribe(() => {
      window.location.reload();
    });
}
